import { useEffect } from 'react'
import { OverlayPortal } from './ModalPortal'
import { Check } from 'lucide-react'
import orellaOWhite from '../assets/logos/orella_o_white.png'
import { useAgent } from './AgentContext'

const NAVY = '#15354F'

/**
 * SubmitOverlay — Cinematic 3-phase submit transition.
 * 
 * Phases: 'processing' → 'success' → 'exit'
 * 
 * v155.0.6: Suppresses the OrellaFAB while overlay is visible.
 * 
 * Usage:
 *   const [submitPhase, setSubmitPhase] = useState(null)
 *   
 *   async function handleSubmit() {
 *     setSubmitPhase('processing')
 *     await doWork()
 *     await delay(1200)
 *     setSubmitPhase('success')
 *     await delay(800)
 *     setSubmitPhase('exit')
 *     await delay(500)
 *     navigate('/next')
 *   }
 * 
 *   <SubmitOverlay
 *     phase={submitPhase}
 *     processingTitle="Saving your acknowledgment..."
 *     processingSubtitle={t('diagnose.updatingYourCarePlan')}
 *     successTitle="Diagnose Complete!"
 *     successSubtitle={t('diagnose.headingToYourCarePlan')}
 *   />
 * 
 * @param {string|null} phase - null | 'processing' | 'success' | 'exit'
 * @param {string} processingTitle
 * @param {string} processingSubtitle
 * @param {string} successTitle
 * @param {string} successSubtitle
 */
export default function SubmitOverlay({
  phase,
  // v214.9.1 (HOTFIX): Defaults previously called a bare `t(...)` that was never
  // imported — caused "Can't find variable: t" the moment the overlay rendered
  // unless ALL four props were passed explicitly. Use plain English strings as
  // safe defaults; callers should pass localized strings via t() at the call site.
  processingTitle = 'Submitting...',
  processingSubtitle = 'Preparing the next step',
  successTitle = 'Complete',
  successSubtitle = 'Moving on',
}) {
  // Suppress FAB during overlay
  const { suppressFab } = useAgent()
  useEffect(() => {
    if (phase) {
      suppressFab?.(true)
      return () => suppressFab?.(false)
    }
  }, [!!phase])

  if (!phase) return null

  return (
    <>
      <style>{`
        @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes overlayExit { from { transform: translateY(0); } to { transform: translateY(-100%); } }
        @keyframes iconPulse { 0%, 100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.06); opacity: 1; } }
        @keyframes ringExpand { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.25); opacity: 0; } }
        @keyframes dotBounce { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }
        @keyframes checkPop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
      `}</style>
      <OverlayPortal>
      <div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
        style={{
          backgroundColor: NAVY,
          animation: phase === 'exit'
            ? 'overlayExit 0.5s ease-in forwards'
            : 'overlayIn 0.3s ease-out',
        }}
      >
        {phase === 'processing' && (
          <>
            <div className="relative mb-6">
              <div
                className="absolute -inset-3 rounded-3xl border-2"
                style={{
                  borderColor: 'rgba(70,168,249,0.3)',
                  animation: 'ringExpand 1.5s ease-in-out infinite'
                }}
              />
              <div
                className="w-20 h-20 rounded-[22px] flex items-center justify-center"
                style={{
                  background: 'rgba(70,168,249,0.15)',
                  animation: 'iconPulse 1.5s ease-in-out infinite'
                }}
              >
                <img src={orellaOWhite} alt="" className="w-12 h-12 object-contain" />
              </div>
            </div>
            <p className="text-white font-bold text-[17px] mb-1.5">{processingTitle}</p>
            <p className="text-white/40 text-[13px]">{processingSubtitle}</p>
            <div className="flex gap-1.5 mt-5">
              {[0, 0.2, 0.4].map((delay, i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: 'rgba(70,168,249,0.4)',
                    animation: `dotBounce 1.2s ease-in-out infinite ${delay}s`
                  }}
                />
              ))}
            </div>
          </>
        )}

        {phase === 'success' && (
          <>
            <div
              className="w-20 h-20 rounded-[22px] flex items-center justify-center mb-6"
              style={{ background: 'rgba(102,209,112,0.2)' }}
            >
              <Check
                size={40}
                strokeWidth={2.5}
                className="text-green-400"
                style={{ animation: 'checkPop 0.4s ease-out' }}
              />
            </div>
            <p className="text-green-400 font-bold text-[17px] mb-1.5">{successTitle}</p>
            <p className="text-white/40 text-[13px]">{successSubtitle}</p>
          </>
        )}

        {phase === 'exit' && (
          <>
            <div
              className="w-20 h-20 rounded-[22px] flex items-center justify-center mb-6"
              style={{ background: 'rgba(102,209,112,0.2)' }}
            >
              <Check size={40} strokeWidth={2.5} className="text-green-400" />
            </div>
          </>
        )}
      </div>
      </OverlayPortal>
    </>
  )
}

/**
 * Helper: run the 3-phase overlay sequence.
 * 
 * Usage:
 *   await runOverlaySequence(setSubmitPhase, async () => {
 *     await apiCall()
 *   }, () => navigate('/case/home'))
 */
export async function runOverlaySequence(setPhase, asyncWork, onComplete) {
  setPhase('processing')
  try {
    await asyncWork()
    // Ensure minimum display time — v171.12: increased for more satisfying feel
    await new Promise(r => setTimeout(r, 1800))
    setPhase('success')
    await new Promise(r => setTimeout(r, 1200))
    setPhase('exit')
    await new Promise(r => setTimeout(r, 500))
    onComplete()
  } catch (err) {
    console.error('SubmitOverlay sequence error:', err)
    setPhase(null)
    throw err
  }
}
